import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import { useGetPermissionLineageQuery } from "permissions/_api/permissions-api-service";
import { grantPeopleIds } from "permissions/_views/grant-row/grant-row-model";
import { grantHistoryEntries } from "permissions/_views/people/grant-history-model";
import { usePersonNames } from "permissions/_views/targets/person-name-states";
import { grantHistoryWording } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import { useMemo } from "react";

/**
 * The details pane's history of one grant (#6825): every grant it replaced, newest first, so a reader
 * sees how often the access was removed and given back, when, and by whom.
 */
export default function GrantHistory(props: Readonly<{ permissionId: Guid; viewerUserId: Guid; today?: AHDate }>) {
  const lineage = useGetPermissionLineageQuery(props.permissionId.value);
  // Memoized on purpose: the people are looked up from these, and a fresh set each render would be a
  // fresh lookup argument.
  const replaced = useMemo(() => Permission.fromJSONArray(lineage.data?.replaced ?? []), [lineage.data]);
  const peopleIds = useMemo(() => new Set(replaced.flatMap(grantPeopleIds)), [replaced]);
  const names = usePersonNames(peopleIds);
  const entries = grantHistoryEntries(replaced, props.viewerUserId, names, props.today ?? AHDate.TODAY);

  let summary: string = grantHistoryWording.loading;
  if (lineage.isError) summary = grantHistoryWording.failed;
  else if (lineage.data) summary = entries.length === 0 ? grantHistoryWording.none : grantHistoryWording.count(entries.length);

  return (
    <>
      <Typography component="dt" variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        {grantHistoryWording.term}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {summary}
        {entries.length > 0 && (
          <Box component="ol" aria-label={grantHistoryWording.term} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {entries.map((entry) => (
              <li key={entry.id}>
                {entry.granted}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block" }}>
                  {entry.removed}
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Typography>
    </>
  );
}
